import { createContext, useEffect, useState, useContext } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import API from "../api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const isDev = process.env.NODE_ENV === "development";

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [orgSlug, setOrgSlug] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  // =====================================================
  // 🔁 RESTORE FROM LOCAL STORAGE (RUN ONCE)
  // =====================================================
  useEffect(() => {
    const savedRole = localStorage.getItem("role");
    const savedOrgId = localStorage.getItem("orgId");
    const savedOrgSlug = localStorage.getItem("orgSlug");

    if (savedRole && savedOrgSlug) {
      setRole(savedRole);
      setOrgId(savedOrgId);
      setOrgSlug(savedOrgSlug);
    }
  }, []);

  // =====================================================
  // 🔐 FIREBASE AUTH LISTENER
  // =====================================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {

      // =========================
      // ❌ NOT LOGGED IN
      // =========================
      if (!currentUser) {
        setUser(null);
        setRole(null);
        setOrgId(null);
        setOrgSlug(null);
        setOrganizations([]);
        setEmail(null);

        localStorage.removeItem("role");
        localStorage.removeItem("orgId");
        localStorage.removeItem("orgSlug");
        localStorage.removeItem("orgList");
        setLoading(false);
        return;
      }

      try {
        // ✅ Set user immediately
        setUser(currentUser);

        // 🔐 GET TOKEN
        const token = await currentUser.getIdToken();

        // 🔥 VERIFY TOKEN
        const res = await API.post(
          "verify-token/",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (isDev) console.log("🔥 AUTH RESPONSE:", res.data);

        const orgs = Array.isArray(res.data.organizations)
          ? res.data.organizations
          : [];

        setEmail(res.data.email);
        setOrganizations(orgs);

        // 🔥 CLEAR OLD ORG LIST
        localStorage.removeItem("orgList");

        // =====================================================
        // 🔥 SUPER ADMIN
        // =====================================================
        if (res.data.isSuperAdmin) {
          setRole("super_admin");

          if (orgs.length > 0) {
            setOrgId(orgs[0].orgId);
            setOrgSlug(orgs[0].orgSlug);
          }

          return;
        }

        // =====================================================
        // ❌ NO ORGANIZATION
        // =====================================================
        if (orgs.length === 0) {
          setRole("pending");
          setOrgId(null);
          setOrgSlug(null);
          return;
        }

        // =====================================================
        // ✅ SINGLE ORGANIZATION
        // =====================================================
        if (orgs.length === 1) {
          const org = orgs[0];

          setRole(org.role);
          setOrgId(org.orgId);
          setOrgSlug(org.orgSlug);

          // 🔥 SAVE TO LOCAL STORAGE
          localStorage.setItem("role", org.role);
          localStorage.setItem("orgId", org.orgId);
          localStorage.setItem("orgSlug", org.orgSlug);

          return;
        }

        // =====================================================
        // 🔥 MULTIPLE ORGANIZATIONS
        // =====================================================
        if (orgs.length > 1) {

           setRole("multi_org");
           setOrgId(null);
           setOrgSlug(null);
           setOrgSlug(null);
           localStorage.setItem("orgList", JSON.stringify(orgs));
           return;
           }
      } catch (err) {
        if (isDev) console.error("❌ Auth error:", err);

        setUser(null);
        setRole(null);
        setOrgId(null);
        setOrgSlug(null);
        setOrganizations([]);
        setEmail(null);

        localStorage.removeItem("role");
        localStorage.removeItem("orgId");
        localStorage.removeItem("orgSlug");
        localStorage.removeItem("orgList");
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        orgId,
        orgSlug,
        organizations,
        loading,
        email,
        setRole,
        setOrgId,
        setOrgSlug,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};